import { Box, TableCell } from "@mui/material";
import TableRow from "@common/components/tableRow/TableRow";
import {
    DotLegalBreadCrumbModel,
    DotLegalButton,
    DotLegalEmptyState,
    DotLegalOverflowMenu,
    DotLegalPageHeader,
    ITableHeader,
} from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import Rocket from "../../processingActivity/processingActivities/rocket.svg?react";
import RiskScenarioDialog from "./riskScenarioDialog/RiskScenarioDialog";
import RiskScenarioCatalogDialog from "./riskScenarioCatalogDialog/RiskScenarioCatalogDialog";
import { DeleteRiskScenarioDialog } from "./deleteRiskScenarioDialog/DeleteRiskScenarioDialog";
import { EntityType } from "@common/Common.types";
import { getAppliesToLabel, toSaveModel, useRiskScenariosDataMapping } from "./RiskScenarios.hooks";
import { RiskScenarioViewModel } from "./RiskScenarios.types";
import CheckIcon from "@mui/icons-material/Check";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

function RiskScenarios() {
    const { translateString, translateRiskScenarioCategory, translateEntityType } = useTranslation();
    const { permissions } = useUserContext();
    const {
        isLoading,
        data,
        hasRiskScenarios,
        hasCatalogItemsToImport,
        showRiskScenarioDialog,
        setShowRiskScenarioDialog,
        showCatalogDialog,
        setShowCatalogDialog,
        riskScenarioForDeletion,
        setRiskScenarioForDeletion,
        selectedRiskScenario,
        setSelectedRiskScenario,
        getFilterDataOptions,
        mapSearchFilter,
        onSave,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useRiskScenariosDataMapping();
    const breadCrumbs: Array<DotLegalBreadCrumbModel> = [{ name: translateString("riskScenarios") }];

    const getMenuItems = (riskScenario: RiskScenarioViewModel) => {
        let menuItems = [];

        if (permissions.canManageMasterData) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setSelectedRiskScenario(toSaveModel(riskScenario));
                    setShowRiskScenarioDialog(true);
                },
            });

            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => {
                    setRiskScenarioForDeletion(riskScenario);
                },
            });
        }

        return menuItems;
    };

    const openCreateDialog = () => {
        setSelectedRiskScenario(undefined);
        setShowRiskScenarioDialog(true);
    };

    return (
        <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
            <DotLegalPageHeader
                breadCrumbs={breadCrumbs}
                rightComponent={
                    permissions.canManageMasterData ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                            {hasCatalogItemsToImport && (
                                <DotLegalButton buttonType={"secondary"} size="medium" onClick={() => setShowCatalogDialog(true)}>
                                    {translateString("riskScenarioCatalog")}
                                </DotLegalButton>
                            )}
                            <DotLegalButton buttonType={"primary"} size="medium" onClick={openCreateDialog}>
                                {translateString("createRiskScenario")}
                            </DotLegalButton>
                        </Box>
                    ) : undefined
                }
            />

            <TablePagePaper>
                {hasRiskScenarios ? (
                    <DotLegalTableWithFilterBar
                        internalScroll
                        filterBar={{
                            isLoading: isLoading,
                            filterData: getFilterDataOptions(),
                            fetchFilterData: true,
                            onMenuClose: (options) => mapSearchFilter(options),
                            onRemove: (options) => mapSearchFilter(options),
                            pinnedFilterIds: pinnedFilterIds,
                            onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                        }}
                        headers={getHeaders(translateString)}
                        rowsPerPage={30}
                        hasOverflowMenu
                        defaultOrderBy={"name"}
                        defaultOrder={"asc"}
                        isLoading={isLoading}
                        clickableRows={permissions.canManageMasterData}
                        emptyText={translateString("noRiskScenariosMatchesQuery")}
                        data={data}
                        renderRow={(row: RiskScenarioViewModel) => (
                            <TableRow
                                onClick={
                                    permissions.canManageMasterData
                                        ? () => {
                                              setSelectedRiskScenario(toSaveModel(row));
                                              setShowRiskScenarioDialog(true);
                                          }
                                        : undefined
                                }
                                key={row.id}
                            >
                                <TableCell>
                                    <Box sx={(theme) => ({ color: theme.palette.primary.main, fontWeight: theme.typography.fontWeightBold })}>{row.name}</Box>
                                </TableCell>
                                <TableCell>{row.description}</TableCell>
                                <TableCell>{translateRiskScenarioCategory(row.category)}</TableCell>
                                <TableCell>
                                    {row.appliesToAllRiskAreas ? translateString("allRiskAreas") : row.riskAreas.map((riskArea) => riskArea.name).join(", ")}
                                </TableCell>
                                <TableCell>{getAppliesToLabel(row, translateString, translateEntityType)}</TableCell>
                                <TableCell>
                                    <Box>{row.createdFromObservation && <CheckIcon />}</Box>
                                </TableCell>
                                <TableCell align={"right"}>
                                    <DotLegalOverflowMenu items={getMenuItems(row)} />
                                </TableCell>
                            </TableRow>
                        )}
                    />
                ) : (
                    <DotLegalEmptyState icon={<Rocket />} text={translateString("createRiskScenarioToGetStarted")} />
                )}
            </TablePagePaper>

            {showRiskScenarioDialog && (
                <RiskScenarioDialog onCloseDialog={() => setShowRiskScenarioDialog(false)} onSave={onSave} selectedRiskScenario={selectedRiskScenario} />
            )}

            {riskScenarioForDeletion && (
                <DeleteRiskScenarioDialog
                    selectedRiskScenario={riskScenarioForDeletion!}
                    onDeletion={onSave}
                    onCloseDialog={() => setRiskScenarioForDeletion(undefined)}
                />
            )}

            {showCatalogDialog && <RiskScenarioCatalogDialog onHideDialog={() => setShowCatalogDialog(false)} onImported={onSave} />}
        </Box>
    );
}

export default RiskScenarios;

function getHeaders(translateString: (key: string) => string) {
    let headers = Array<ITableHeader<RiskScenarioViewModel>>();
    headers.push({ property: "name", text: translateString("riskScenario"), align: "left", showOnMobile: true });
    headers.push({ property: "description", text: translateString("description"), align: "left", showOnMobile: true });
    headers.push({ property: "category", text: translateString("category"), align: "left", showOnMobile: true });
    headers.push({ property: "riskAreas", text: translateString("riskAreas"), align: "left", showOnMobile: true });
    headers.push({ property: "entityTypes", text: translateString("types"), align: "left", showOnMobile: true });
    headers.push({ property: "createdFromObservation", text: translateString("createdFromObservation"), align: "left", showOnMobile: true });

    return headers;
}
